import {PayoutCreditorKind} from '@sollapay/enums';
import {getStartOfWeek, parseMoney} from '@sollapay/utils';

import type {PayoutsFilterState} from '../config/payouts-filter.config';
import type {PayoutInstructionDTO} from '@sollapay/types';

export function countPayoutsActiveFilters(filter: PayoutsFilterState): number {
  let count = 0;
  if (filter.datePreset !== 'all') count++;
  if (filter.amountMin != null || filter.amountMax != null) count++;
  if (filter.statuses.length > 0) count++;
  if (filter.roles.length > 0) count++;
  return count;
}

export function isPayoutsFilterActive(filter: PayoutsFilterState): boolean {
  return countPayoutsActiveFilters(filter) > 0;
}

export interface SearchablePayout {
  payout: PayoutInstructionDTO;
  searchFields: string[];
}

/** Recompute only when `payouts` itself changes, not on every search keystroke. */
export function buildSearchablePayouts(payouts: PayoutInstructionDTO[]): SearchablePayout[] {
  return payouts.map(payout => ({payout, searchFields: buildSearchFields(payout)}));
}

function buildSearchFields(payout: PayoutInstructionDTO): string[] {
  const description =
    payout.creditor.kind === PayoutCreditorKind.BENEFICIARY ? payout.creditor.description : null;
  const municipalityName =
    payout.creditor.kind === PayoutCreditorKind.AUTHORITY ? payout.creditor.municipalityName : null;
  return [payout.creditor.name, municipalityName, description, payout.referenceCode]
    .filter((field): field is string => !!field)
    .map(field => field.toLowerCase());
}

export function filterPayouts(
  searchablePayouts: SearchablePayout[],
  filter: PayoutsFilterState,
  search: string
): PayoutInstructionDTO[] {
  const searchLower = search.toLowerCase().trim();
  return searchablePayouts
    .filter(({payout, searchFields}) => matchesFilter(payout, searchFields, filter, searchLower))
    .map(({payout}) => payout);
}

function resolveDatePresetCutoff(datePreset: PayoutsFilterState['datePreset']): Date | null {
  if (datePreset === 'all') return null;
  if (datePreset === 'this_week') return getStartOfWeek(new Date());
  const days = datePreset === '7d' ? 7 : datePreset === '30d' ? 30 : 90;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  return cutoff;
}

function matchesFilter(
  payout: PayoutInstructionDTO,
  searchFields: string[],
  filter: PayoutsFilterState,
  search: string
): boolean {
  if (search.length > 0 && !searchFields.some(field => field.includes(search))) return false;
  const dateCutoff = resolveDatePresetCutoff(filter.datePreset);
  if (dateCutoff && new Date(payout.createdAt) < dateCutoff) return false;
  if (filter.statuses.length > 0 && !filter.statuses.includes(payout.status)) return false;
  if (filter.roles.length > 0) {
    const role =
      payout.creditor.kind === PayoutCreditorKind.BENEFICIARY ? payout.creditor.role : null;
    if (!role || !filter.roles.includes(role)) return false;
  }
  if (filter.amountMin != null || filter.amountMax != null) {
    const amount = parseMoney(payout.amount).toNumber();
    if (filter.amountMin != null && amount < filter.amountMin) return false;
    if (filter.amountMax != null && amount > filter.amountMax) return false;
  }
  return true;
}
